<template>
  <div class="login-container">
    <!-- Background with gradient and pattern -->
    <div class="login-background">
      <div class="gradient-overlay"></div>
      <div class="pattern-overlay"></div>
    </div>

    <v-container fill-height class="login-content d-flex align-center">
      <v-row align="center" justify="center" no-gutters class="w-100">
        <v-col cols="12" sm="10" md="8" lg="6" xl="4">
          <v-card
            elevation="24"
            class="login-card mx-auto"
            rounded="xl"
          >
            <!-- Header Section -->
            <div class="login-header">
              <div class="text-center py-8">
                <div class="logo-container mb-4">
                  <div class="simple-logo">
                    <span class="logo-vijan">Vijan</span><span class="logo-separator">.</span><span class="logo-ai">AI</span>
                  </div>
                </div>
                <p class="subtitle-1 text-center brand-subtitle">
                  🚀 AI Agents Marketplace • Unlock Intelligent Solutions
                </p>
                <div class="feature-highlights mt-3">
                  <v-chip
                    small
                    outlined
                    color="white"
                    text-color="white"
                    class="feature-chip mr-2 mb-1"
                  >
                    <v-icon left small>mdi-brain</v-icon>
                    Smart AI
                  </v-chip>
                  <v-chip
                    small
                    outlined
                    color="white"
                    text-color="white"
                    class="feature-chip mr-2 mb-1"
                  >
                    <v-icon left small>mdi-lightning-bolt</v-icon>
                    Lightning Fast
                  </v-chip>
                  <v-chip
                    small
                    outlined
                    color="white"
                    text-color="white"
                    class="feature-chip mb-1"
                  >
                    <v-icon left small>mdi-shield-check</v-icon>
                    Secure
                  </v-chip>
                </div>
              </div>
            </div>

            <!-- Login Form -->
            <v-card-text class="px-8 pb-8">
              <div class="text-center mb-6">
                <h2 class="text-h5 font-weight-medium mb-2 welcome-text">Welcome to the Future</h2>
                <p class="text-body-2 welcome-subtitle">Access powerful AI agents and transform your workflow</p>
                <div class="stats-row mt-3">
                  <div class="stat-item">
                    <div class="stat-number">500+</div>
                    <div class="stat-label">AI Agents</div>
                  </div>
                  <div class="stat-divider">•</div>
                  <div class="stat-item">
                    <div class="stat-number">10k+</div>
                    <div class="stat-label">Users</div>
                  </div>
                  <div class="stat-divider">•</div>
                  <div class="stat-item">
                    <div class="stat-number">99.9%</div>
                    <div class="stat-label">Uptime</div>
                  </div>
                </div>
              </div>

              <v-form @submit.prevent="login">
                <v-text-field
                  v-model="form.email"
                  label="Email Address"
                  placeholder="Enter your email"
                  type="email"
                  prepend-inner-icon="mdi-email-outline"
                  outlined
                  dense
                  class="mb-2"
                  :error-messages="errors.email"
                  background-color="grey lighten-5"
                ></v-text-field>

                <v-text-field
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  label="Password"
                  placeholder="Enter your password"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  outlined
                  dense
                  class="mb-4"
                  :error-messages="errors.password"
                  background-color="grey lighten-5"
                  @click:append="showPassword = !showPassword"
                  @keypress.enter="login"
                ></v-text-field>

                <v-btn
                  :loading="isLoading"
                  :disabled="isLoading"
                  color="primary"
                  large
                  block
                  rounded
                  elevation="2"
                  class="mb-4 login-btn"
                  @click="login"
                >
                  <v-icon left>mdi-login</v-icon>
                  Sign In
                </v-btn>

                <div class="text-center">
                  <v-btn
                    text
                    small
                    color="primary"
                    to="/forgot-password"
                    class="text-capitalize"
                  >
                    Forgot your password?
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>

            <!-- Demo Users Info -->
            <v-divider class="mx-8"></v-divider>
            <v-card-text class="px-8 py-4">
              <div class="text-center">
                <p class="text-caption demo-text mb-2">🎯 Quick Demo Access - Experience AI Power Instantly!</p>
                <div class="demo-buttons">
                  <v-chip
                    small
                    outlined
                    class="ma-1 demo-chip"
                    @click="setDemoCredentials('admin')"
                  >
                    <v-icon left small>mdi-crown</v-icon>
                    Admin Demo
                    <v-tooltip activator="parent" bottom>
                      <span>Full platform access with all AI agents</span>
                    </v-tooltip>
                  </v-chip>
                </div>
                <p class="text-caption demo-password mt-2">🔑 Auto-fills credentials • Password: 123456</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "LoginPage",
  data() {
    return {
      form: {
        email: "",
        password: "",
        portal: true,
      },
      showPassword: false,
      isLoading: false,
    };
  },
  methods: {
    ...mapActions({
      logIn: "auth/logIn",
    }),
    setDemoCredentials(type) {
      if (type === 'admin') {
        this.form.email = 'admin@gmail.com';
        this.form.password = '123456';
      }
    },
    async login() {
      if (!this.form.email || !this.form.password) {
        this.$store.dispatch('errors/setErrors', {
          email: !this.form.email ? ['Email is required'] : [],
          password: !this.form.password ? ['Password is required'] : []
        });
        return;
      }

      this.isLoading = true;
      
      try {
        await this.logIn(this.form);
        this.$router.push('/');
      } catch (error) {
        console.error('Login error:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Invalid email or password. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
}

.login-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0.95;
}


.pattern-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.15) 3px, transparent 3px),
    radial-gradient(circle at 80% 50%, rgba(240, 147, 251, 0.2) 2px, transparent 2px),
    radial-gradient(circle at 40% 20%, rgba(79, 172, 254, 0.15) 2px, transparent 2px),
    radial-gradient(circle at 60% 80%, rgba(245, 87, 108, 0.1) 2px, transparent 2px);
  background-size: 80px 80px, 60px 60px, 100px 100px, 120px 120px;
  animation: float 20s ease-in-out infinite, drift 30s linear infinite;
}

@keyframes drift {
  0% { transform: translate(0, 0); }
  25% { transform: translate(-10px, -10px); }
  50% { transform: translate(10px, -5px); }
  75% { transform: translate(-5px, 10px); }
  100% { transform: translate(0, 0); }
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
}

.login-content {
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.login-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  max-width: 450px;
  transform: translateY(0);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
}

.login-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  z-index: 1;
}

.login-card:hover {
  transform: translateY(-8px);
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 0 40px rgba(240, 147, 251, 0.2),
    0 0 80px rgba(79, 172, 254, 0.1);
}

.login-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
  position: relative;
  overflow: hidden;
}

.login-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%);
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { transform: translateX(-100%); }
  50% { transform: translateX(100%); }
}

.simple-logo {
  display: inline-block;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 600;
  font-size: 42px;
  line-height: 1;
  transition: all 0.3s ease;
  cursor: default;
  letter-spacing: 1px;
}

.simple-logo:hover {
  transform: scale(1.05);
}

.logo-vijan {
  color: #ffffff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.logo-separator {
  color: #f093fb;
  margin: 0 2px;
}

.logo-ai {
  color: white;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

@keyframes pulse-bright {
  0%, 100% { 
    filter: drop-shadow(0 0 30px rgba(255, 215, 0, 1)) 
            drop-shadow(0 0 40px rgba(255, 107, 53, 0.8)) 
            drop-shadow(0 0 50px rgba(255, 23, 68, 0.6))
            drop-shadow(0 0 60px rgba(240, 147, 251, 0.4));
  }
  50% { 
    filter: drop-shadow(0 0 40px rgba(255, 215, 0, 1)) 
            drop-shadow(0 0 50px rgba(255, 107, 53, 1)) 
            drop-shadow(0 0 60px rgba(255, 23, 68, 0.8))
            drop-shadow(0 0 70px rgba(240, 147, 251, 0.6));
  }
}

@keyframes separator-glow {
  0% { 
    filter: drop-shadow(0 0 20px rgba(255, 215, 0, 1)) drop-shadow(0 0 30px rgba(255, 215, 0, 0.8));
  }
  100% { 
    filter: drop-shadow(0 0 30px rgba(255, 215, 0, 1)) drop-shadow(0 0 40px rgba(255, 215, 0, 1));
  }
}

@keyframes glow-bright {
  0% { 
    filter: drop-shadow(0 0 30px rgba(0, 212, 255, 1)) 
            drop-shadow(0 0 40px rgba(91, 115, 255, 0.8))
            drop-shadow(0 0 50px rgba(142, 36, 170, 0.6))
            drop-shadow(0 0 60px rgba(156, 39, 176, 0.4));
  }
  100% { 
    filter: drop-shadow(0 0 40px rgba(0, 212, 255, 1)) 
            drop-shadow(0 0 50px rgba(91, 115, 255, 1))
            drop-shadow(0 0 60px rgba(142, 36, 170, 0.8))
            drop-shadow(0 0 70px rgba(156, 39, 176, 0.6));
  }
}

@keyframes glow {
  0% { filter: drop-shadow(0 0 20px rgba(240, 147, 251, 0.8)); }
  100% { filter: drop-shadow(0 0 30px rgba(240, 147, 251, 1)) drop-shadow(0 0 40px rgba(79, 172, 254, 0.6)); }
}

.brand-text {
  background: linear-gradient(45deg, #fff 0%, #f093fb 50%, #4facfe 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: none;
  animation: brandGlow 3s ease-in-out infinite;
}

@keyframes brandGlow {
  0%, 100% {
    background: linear-gradient(45deg, #fff 0%, #f093fb 50%, #4facfe 100%);
  }
  50% {
    background: linear-gradient(45deg, #4facfe 0%, #fff 50%, #f093fb 100%);
  }
}

.brand-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  font-size: 18px;
}

.feature-highlights {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}

.feature-chip {
  border-color: rgba(255, 255, 255, 0.6) !important;
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  font-weight: 500 !important;
  font-size: 12px !important;
}

.feature-chip:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.9) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
}

.welcome-text {
  background: linear-gradient(45deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
}

.welcome-subtitle {
  color: #6c757d;
  font-weight: 500;
  font-size: 16px;
}

.stats-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(240, 147, 251, 0.05) 100%);
  border-radius: 12px;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 18px;
  font-weight: 700;
  background: linear-gradient(45deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  margin-top: 2px;
}

.stat-divider {
  color: #cbd5e1;
  font-weight: bold;
  font-size: 16px;
}

.demo-text {
  color: #f093fb;
  font-weight: 600;
  font-size: 14px;
}

.demo-password {
  color: #64748b;
  font-weight: 500;
  font-size: 12px;
}

.demo-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

.demo-chip {
  transition: all 0.3s ease;
  border-width: 2px !important;
  font-weight: 600 !important;
}

.login-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white;
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.login-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s;
}

.login-btn:hover::before {
  left: 100%;
}

.login-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4), 0 0 25px rgba(118, 75, 162, 0.3);
}

.login-btn:before {
  background-color: transparent;
}

/* Form field styling */
.v-text-field--outlined >>> .v-input__control .v-input__slot {
  border-radius: 15px;
  transition: all 0.3s ease;
  border-width: 2px;
}

.v-text-field--outlined >>> .v-input__control .v-input__slot:hover {
  border-color: #f093fb;
  box-shadow: 0 4px 15px rgba(240, 147, 251, 0.2);
  transform: translateY(-1px);
}

.v-text-field--outlined.v-input--is-focused >>> .v-input__control .v-input__slot {
  border-color: #f093fb;
  box-shadow: 0 6px 20px rgba(240, 147, 251, 0.3);
}

/* Chip styling */
.v-chip.v-chip--outlined {
  border-color: #f093fb;
  color: #f093fb;
  transition: all 0.3s ease;
  cursor: pointer;
  border-width: 2px;
  font-weight: 600;
}

.v-chip.v-chip--outlined:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  border-color: transparent;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .login-card {
    margin: 12px;
    max-width: none;
  }
}

@media (max-width: 600px) {
  .login-container {
    min-height: 100vh;
    padding: 0;
  }

  .login-content {
    padding: 16px !important;
    min-height: 100vh;
  }

  .login-card {
    margin: 8px;
    max-width: none;
    width: 100%;
    border-radius: 16px !important;
  }
  
  .login-header .py-8 {
    padding-top: 24px !important;
    padding-bottom: 24px !important;
  }
  
  .px-8 {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .pb-8 {
    padding-bottom: 24px !important;
  }

  .feature-highlights {
    flex-direction: row;
    justify-content: center;
    gap: 4px;
  }

  .feature-chip {
    font-size: 10px !important;
    padding: 0 8px !important;
    height: 24px !important;
  }

  .stats-row {
    flex-direction: row;
    gap: 12px;
    padding: 12px 8px;
    flex-wrap: wrap;
  }

  .stat-item {
    flex: 1;
    min-width: 60px;
  }

  .stat-number {
    font-size: 16px;
  }

  .stat-label {
    font-size: 10px;
  }

  .stat-divider {
    display: none;
  }

  .demo-buttons {
    justify-content: center;
  }

  .demo-chip {
    min-width: 120px;
    justify-content: center;
  }

  .brand-subtitle {
    font-size: 14px;
    line-height: 1.4;
  }

  .simple-logo {
    font-size: 28px;
  }

  .welcome-text {
    font-size: 1.25rem !important;
  }

  .welcome-subtitle {
    font-size: 14px;
  }

  .v-text-field {
    margin-bottom: 8px !important;
  }

  .login-btn {
    height: 48px !important;
    font-size: 16px !important;
    min-height: 44px !important;
  }

  /* Improve touch targets */
  .v-btn {
    min-height: 44px !important;
  }

  .demo-chip {
    min-height: 44px !important;
  }

  /* Better form field spacing */
  .v-text-field--outlined >>> .v-input__control .v-input__slot {
    min-height: 48px !important;
  }

  .v-card-text .mb-6 {
    margin-bottom: 20px !important;
  }

  .v-card-text .mb-4 {
    margin-bottom: 16px !important;
  }
}

@media (max-width: 400px) {
  .login-card {
    margin: 4px;
    border-radius: 12px !important;
  }

  .px-8 {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .simple-logo {
    font-size: 24px;
  }

  .brand-subtitle {
    font-size: 12px;
  }

  .feature-chip {
    font-size: 9px !important;
    padding: 0 6px !important;
    height: 22px !important;
  }

  .stats-row {
    padding: 8px 4px;
    gap: 8px;
  }

  .stat-number {
    font-size: 14px;
  }

  .stat-label {
    font-size: 9px;
  }

  .welcome-text {
    font-size: 1.1rem !important;
  }

  .demo-chip {
    min-width: 100px;
    font-size: 11px !important;
  }
}

/* Animation for card entrance */
.login-card {
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
